import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/apiClient';

interface MaintenanceContextType {
  isMaintenanceMode: boolean;
  isMaintenanceLoading: boolean;
  setMaintenanceMode: (enabled: boolean) => void;
}

const MaintenanceContext = createContext<MaintenanceContextType | undefined>(undefined);

export const MaintenanceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(() => {
    // Check localStorage for maintenance mode preference as a fallback
    try {
      const saved = localStorage.getItem('maintenanceMode');
      return saved ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  });
  const [isMaintenanceLoading, setIsMaintenanceLoading] = useState(true);

  // Fetch maintenance mode status from API on mount
  useEffect(() => {
    let isMounted = true;

    const fetchMaintenanceStatus = async () => {
      try {
        const response = await api.get('/health/status');
        const maintenanceMode = response.data?.maintenanceMode;

        if (isMounted && typeof maintenanceMode === 'boolean') {
          setIsMaintenanceMode(maintenanceMode);
        }
      } catch (error) {
        // If API call fails, fall back to localStorage value
        console.error('Failed to fetch maintenance status:', error);
      } finally {
        if (isMounted) {
          setIsMaintenanceLoading(false);
        }
      }
    };

    fetchMaintenanceStatus();

    return () => {
      isMounted = false;
    };
  }, []);

  // Persist maintenance mode changes to localStorage
  useEffect(() => {
    localStorage.setItem('maintenanceMode', JSON.stringify(isMaintenanceMode));
  }, [isMaintenanceMode]);

  return (
    <MaintenanceContext.Provider
      value={{
        isMaintenanceMode,
        isMaintenanceLoading,
        setMaintenanceMode: setIsMaintenanceMode,
      }}
    >
      {children}
    </MaintenanceContext.Provider>
  );
};

export const useMaintenance = () => {
  const context = useContext(MaintenanceContext);
  if (!context) {
    throw new Error('useMaintenance must be used within MaintenanceProvider');
  }
  return context;
};
